import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { BorderGradient } from 'processhub-components-lib';
import { FiX } from 'react-icons/fi';

interface AddTaxModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (taxData: { id: string; region: string; rate: string; effectiveDate: string }) => void;
  defaultValues?: {
    id: string;
    region: string;
    rate: string;
    effectiveDate: string;
  } | null;
  mode: 'add' | 'edit';
}

const AddTaxModal: React.FC<AddTaxModalProps> = ({ isOpen, onClose, onSave, defaultValues, mode }) => {
  const [formData, setFormData] = useState({
    id: '',
    region: '',
    rate: '',
    effectiveDate: '',
  });

  useEffect(() => {
    if (defaultValues) {
      setFormData(defaultValues);
    } else {
      setFormData({
        id: Math.random().toString(36).substring(2, 9),
        region: '',
        rate: '',
        effectiveDate: '',
      });
    }
  }, [defaultValues]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      rate: `${formData.rate}%`,
    });
  };

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    const parts = value.split('.');
    const formatted = parts.length > 1 ? `${parts[0]}.${parts[1]}` : value;
    setFormData({ ...formData, rate: formatted });
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] lg:w-[600px]">
          <BorderGradient className="rounded-lg flex flex-col w-full">
            <div className="bg-[#1A1A1A] rounded-lg p-6 relative text-white">
              <button
                onClick={onClose}
                className="absolute top-[28px] right-4 text-gray-400 hover:text-white"
              >
                <FiX size={24} />
              </button>

              <h2 className="text-lg font-semibold mb-6">
                {mode === 'edit' ? 'Edit Tax Rate' : 'Add Tax Rate'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Region</label>
                  <input
                    type="text"
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    className="w-full bg-[#232323] rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter region name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Rate (%)</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.rate}
                      onChange={handleRateChange}
                      className="w-full bg-[#232323] rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter tax rate"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Effective Date</label>
                  <input
                    type="date"
                    value={formData.effectiveDate}
                    onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
                    className="w-full bg-[#232323] rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex items-center justify-center text-center gap-2 px-4 py-2 w-[140px] text-white rounded-lg hover:bg-[#3a3a3a33] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-cente justify-center gap-2 px-4 py-2 w-[140px] bg-[#2A2A2A] text-white rounded-lg hover:bg-[#3a3a3a33] transition-colors"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </BorderGradient>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AddTaxModal;
